import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';

import { buildBillingPath } from '../../app/Routes/orgRouteUtils';
import { Filter } from './components/Filter';
import { GoToDetails, Table } from '../../components';
import { BillableTime } from './components/BillableTime';
import { useAgentsBilling } from './hooks/useAgentsBilling';
import { formatDate } from '../knowledge-management/TextCompressorNew/utils/utils';

const AgentsBillingPage = () => {
  const { params } = useRouteMatch<{ organization_id: string }>();
  const organizationId = params.organization_id;
  const location = useLocation();
  const history = useHistory();
  const {
    setAgentsFilters,
    agentsFilters,
    agentsSort,
    agents,
    agentsLoading,
    setAgentsSort,
    lastRefreshedDate,
    isLastRefreshedDateLoading,
    ...rest
  } = useAgentsBilling();

  const { totalText, totalVoice, totalVideo } = rest?.rawData || {};

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 m-6">
        <div className="grid grid-cols-1 gap-4 xl:w-1/2">
          <Filter values={agentsFilters ?? {}} onChange={setAgentsFilters} />
        </div>

        <div className="flex flex-col items-center">
          <div>
            <div className="flex flex-row justify-start flex-wrap gap-4 p-4">
              <div className="flex flex-row gap-4 mr-4">
                <div className="flex flex-col">
                  <div className="text-medium text-secondary">
                    Total Billable Voice
                  </div>
                  <div className="flex">
                    <BillableTime time={totalVoice} className="self-end" />
                  </div>
                </div>
              </div>

              <div className="flex flex-row  gap-4 ">
                <div className="flex flex-col">
                  <div className="text-medium text-secondary">
                    Total Billable Text
                  </div>
                  <div className="flex">
                    <BillableTime time={totalText} className="self-end" />
                  </div>
                </div>
              </div>
              <div className="flex flex-row  gap-4 ">
                <div className="flex flex-col">
                  <div className="text-medium text-secondary">
                    Total Billable Video
                  </div>
                  <div className="flex">
                    <BillableTime time={totalVideo} className="self-end" />
                  </div>
                </div>
              </div>
            </div>

            {!isLastRefreshedDateLoading && (
              <div className="flex flex-row justify-start ml-4 mt-4">
                <div className="text-medium flex">
                  <p className="mr-2">Last updated at:</p>
                  <p className="font-semibold">
                    {formatDate(lastRefreshedDate)}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <Table
        sort={agentsSort}
        onSort={setAgentsSort}
        loading={agentsLoading}
        verticalAlign="middle"
        data={agents ?? []}
        emptyMessage="No Billing Information"
        onRowClick={row => {
          history.push(`${buildBillingPath(organizationId, row.agentId)}${location.search}`);
        }}
        config={{
          columns: [
            {
              sortName: 'title',
              header: 'Agent Title',
              align: 'left',
              cellClassName: 'min-w-[12rem] max-w-[14rem]',
              getValue: ({ title }) => <span>{title}</span>,
            },
            {
              header: (
                <div>
                  <p>Billable Voice Time</p>
                  <p>(hh:mm:ss)</p>
                </div>
              ),
              sortName: 'voiceMinutes',
              align: 'center',
              cellClassName: 'min-w-[4rem] w-fit',
              getValue: ({ voiceMinutes }) => (
                <BillableTime
                  className="justify-center"
                  numberClassName="text-medium"
                  format="compact"
                  time={voiceMinutes}
                />
              ),
            },
            {
              header: (
                <div>
                  <p>Billable Text Time</p>
                  <p>(hh:mm:ss)</p>
                </div>
              ),
              sortName: 'textMinutes',
              align: 'center',
              cellClassName: 'min-w-[8rem] w-fit',
              cellContentClassName: 'justify-center',
              getValue: ({ textMinutes }) => (
                <BillableTime
                  className="justify-center"
                  numberClassName="text-medium justify-center"
                  format="compact"
                  time={textMinutes}
                />
              ),
            },
            {
              header: (
                <div>
                  <p>Billable Video Time</p>
                  <p>(hh:mm:ss)</p>
                </div>
              ),
              sortName: 'videoMinutes',
              align: 'center',
              cellClassName: 'min-w-[8rem] w-fit',
              cellContentClassName: 'justify-center',
              getValue: ({ videoMinutes }) => (
                <BillableTime
                  className="justify-center"
                  numberClassName="text-medium justify-center"
                  format="compact"
                  time={videoMinutes}
                />
              ),
            },
            {
              header: '',
              align: 'right',
              cellClassName: 'max-w-[8rem] min-w-[8rem]',
              getValue: ({ agentId }) => {
                const searchParams = location.search;
                return (
                  <GoToDetails url={`${buildBillingPath(organizationId, agentId)}${searchParams}`} />
                );
              },
            },
          ],
        }}
      />
    </>
  );
};

export default AgentsBillingPage;
